import React, { useState, useEffect } from 'react';
import './Notification.css';
import Dashboard from './Dashboard.js';
import { useNavigate } from 'react-router';

const Notification = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalNotifications, setTotalNotifications] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [showUnreadOnly, setShowUnreadOnly] = useState(true);
  const [markingAsRead, setMarkingAsRead] = useState({});

  const navigate = useNavigate();

  const fetchNotifications = async (page = 1, size = pageSize, unreadOnly = showUnreadOnly) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        setError('Authentication token not found');
        setLoading(false);
        return;
      }

      const queryParams = new URLSearchParams({
        page: page.toString(),
        size: size.toString(),
      });

      if (unreadOnly) {
        queryParams.append('is_read', '0');
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}/notification/list-notification?${queryParams.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setNotifications(data.items || []);
        setTotalNotifications(data.total || 0);
        setTotalPages(data.pages || Math.ceil((data.total || 0) / size));
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Failed to fetch notifications');
      }
      setLoading(false);
    } catch (error) {
      setError('Failed to fetch notifications');
      setLoading(false);
      console.error('Error fetching notifications:', error);
    }
  };

  const markNotificationAsRead = async (notificationId) => {
    if (markingAsRead[notificationId]) return; // Prevent double-clicking
    
    setMarkingAsRead(prev => ({ ...prev, [notificationId]: true }));
    
    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        setError('Authentication token not found');
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}/notification/update-notification-seen`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          id: notificationId,
          is_read: true
        })
      });

      if (response.ok) {
        // Update the notification in the local state
        setNotifications(prev => prev.map(notification => 
          notification.notification_id === notificationId 
            ? { ...notification, is_read: true }
            : notification
        ));
        
        // If showing unread only, remove the notification from view
        if (showUnreadOnly) {
          setNotifications(prev => prev.filter(notification => 
            notification.notification_id !== notificationId
          ));
          setTotalNotifications(prev => prev - 1);
        }
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Failed to mark notification as read');
      }
    } catch (error) {
      setError('Failed to mark notification as read');
      console.error('Error marking notification as read:', error);
    } finally {
      setMarkingAsRead(prev => ({ ...prev, [notificationId]: false }));
    }
  };

  const handleNotificationClick = async (notification) => {
    // Mark as read if it's unread
    if (!notification.is_read) {
      await markNotificationAsRead(notification.notification_id);
    }

    // Navigate based on notification type and related_to_type
    if (notification.notification_type === 'job' && notification.related_to_type === 'job_application') {
      // Navigate to job application or job details
      navigate(`/application-detail/${notification.related_to_id}`);
    } else if (notification.related_to_type === 'company') {
      navigate(`/view-company-profile/${notification.related_to_id}`);
    }
    // Add more navigation logic based on your app's routing structure
  };

  const markAllAsRead = async () => {
    const unreadNotifications = notifications.filter(n => !n.is_read);
    
    for (const notification of unreadNotifications) {
      await markNotificationAsRead(notification.notification_id);
    }
  };

  const toggleFilter = (unreadOnly) => {
    setShowUnreadOnly(unreadOnly);
    setCurrentPage(1);
    fetchNotifications(1, pageSize, unreadOnly);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchNotifications(page, pageSize, showUnreadOnly);
  };

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setCurrentPage(1);
    fetchNotifications(1, newSize, showUnreadOnly);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    if (diffInMinutes < 43200) return `${Math.floor(diffInMinutes / 1440)}d ago`;
    
    return date.toLocaleDateString('en-US', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'job':
        return '💼';
      case 'application':
        return '📝';
      case 'company':
        return '🏢';
      case 'system':
        return '⚙️';
      default:
        return '🔔';
    }
  };

  const getNotificationTypeColor = (type) => {
    switch (type) {
      case 'job':
        return 'notification-type--job';
      case 'application':
        return 'notification-type--application';
      case 'company':
        return 'notification-type--company';
      case 'system':
        return 'notification-type--system';
      default:
        return 'notification-type--default';
    }
  };

  useEffect(() => {
    fetchNotifications(1, pageSize, showUnreadOnly);
  }, []);

  const unreadCount = notifications.filter(n => !n.is_read).length;

  return (
    <Dashboard page={"notifications"}>
      <div className="admin-notification-listing-container">
        <div className="admin-notification-listing-content">
          <div className="admin-notification-content-header">
            <div className="admin-notification-header-info">
              <h1>Notifications</h1>
              <p className="admin-notification-date-range-text">
                Showing {notifications.length} of {totalNotifications} notifications
                {showUnreadOnly && ` (${unreadCount} unread)`}
              </p>
            </div>
            {unreadCount > 0 && (
              <button 
                className="admin-notification-mark-all-btn"
                onClick={markAllAsRead}
              >
                Mark All as Read
              </button>
            )}
          </div>

          <div className="admin-notification-list-section">
            <div className="admin-notification-section-header">
              <div className="admin-notification-filter-tabs">
                <button 
                  className={`notification-filter-tab ${showUnreadOnly ? 'active' : ''}`}
                  onClick={() => toggleFilter(true)}
                >
                  Unread ({notifications.filter(n => !n.is_read).length})
                </button>
                <button 
                  className={`notification-filter-tab ${!showUnreadOnly ? 'active' : ''}`}
                  onClick={() => toggleFilter(false)}
                >
                  All Notifications
                </button>
              </div>
            </div>

            <div className="admin-notification-list">
              {loading ? (
                <div className="admin-notification-loading-state">Loading notifications...</div>
              ) : notifications.length > 0 ? (
                notifications.map((notification) => (
                  <div 
                    key={notification.notification_id} 
                    className={`notification-item ${!notification.is_read ? 'unread' : 'read'} ${markingAsRead[notification.notification_id] ? 'marking-read' : ''}`}
                    onClick={() => handleNotificationClick(notification)}
                  >
                    <div className="notification-icon">
                      {getNotificationIcon(notification.notification_type)}
                    </div>
                    
                    <div className="notification-content">
                      <div className="notification-header">
                        <span className={`notification-type-badge ${getNotificationTypeColor(notification.notification_type)}`}>
                          {notification.notification_type.charAt(0).toUpperCase() + notification.notification_type.slice(1)}
                        </span>
                        {!notification.is_read && <span className="unread-indicator"></span>}
                      </div>
                      
                      <div className="notification-message">
                        {notification.content}
                      </div>
                      
                      <div className="notification-meta">
                        <span className="notification-time">
                          {formatDate(notification.created_at)}
                        </span>
                        {notification.related_to_type && (
                          <span className="notification-related">
                            · {notification.related_to_type.replace('_', ' ')}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="notification-actions">
                      {!notification.is_read && (
                        <button
                          className="mark-read-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            markNotificationAsRead(notification.notification_id);
                          }}
                          disabled={markingAsRead[notification.notification_id]}
                          title="Mark as read"
                        >
                          {markingAsRead[notification.notification_id] ? '⟳' : '✓'}
                        </button>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <div className="admin-notification-empty-state">
                  <div className="empty-state-icon">🔔</div>
                  <h3>No notifications found</h3>
                  <p>
                    {showUnreadOnly 
                      ? "You're all caught up! No unread notifications."
                      : "No notifications available."
                    }
                  </p>
                  {showUnreadOnly && (
                    <button 
                      className="view-all-btn"
                      onClick={() => toggleFilter(false)}
                    >
                      View All Notifications
                    </button>
                  )}
                </div>
              )}
            </div>

            {notifications.length > 0 && (
              <div className="admin-notification-pagination">
                <div className="admin-notification-pagination-info">
                  <span>View</span>
                  <select 
                    value={pageSize} 
                    onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                    className="admin-notification-page-size-select"
                  >
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                  </select>
                  <span>notifications per page</span>
                </div>
                
                <div className="admin-notification-pagination-controls">
                  <button 
                    className="admin-notification-page-btn"
                    onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                  >
                    ‹
                  </button>
                  
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                    <button
                      key={page}
                      className={`admin-notification-page-btn ${currentPage === page ? 'active' : ''}`}
                      onClick={() => handlePageChange(page)}
                    >
                      {page}
                    </button>
                  ))}
                  
                  <button 
                    className="admin-notification-page-btn"
                    onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                  >
                    ›
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
        
        {error && (
          <div className="error-message">
            {error}
          </div>
        )}
      </div>
    </Dashboard>
  );
};

export default Notification;